import { type FetchResult, type TypedDocumentNode, useMutation } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { CREATE_WAREHOUSE_LOCATION_MUTATION, GET_WAREHOUSE_LOCATIONS } from 'src/api/warehouses.api';
import { notify } from 'src/lib/notify';
import type { CreateWarehouseLocation, CreateWarehouseLocationInput } from 'types/graphql';

/**
 * What the location form sends. An empty slug goes out as `null`, exactly as before the hook existed: the server
 * rejects it (`slug` is required) and the error toast names it, instead of the client inventing a value.
 */
export type CreateWarehouseLocationFormInput = Omit<CreateWarehouseLocationInput, 'slug'> & { slug: string | null };
type CreateWarehouseLocationFormVariables = { input: CreateWarehouseLocationFormInput };

const CREATE_WAREHOUSE_LOCATION: TypedDocumentNode<CreateWarehouseLocation, CreateWarehouseLocationFormVariables> =
  CREATE_WAREHOUSE_LOCATION_MUTATION;

export function useCreateWarehouseLocation() {
  const [mutate, { loading, error }] = useMutation(CREATE_WAREHOUSE_LOCATION, {
    refetchQueries: [GET_WAREHOUSE_LOCATIONS],
    onCompleted: () => {
      enqueueSnackbar('Location created successfully');
    },
    onError: (mutationError) => {
      notify.error(`Failed to create location: ${mutationError.message}`);
    },
  });
  const create = useCallback(
    ({ input }: CreateWarehouseLocationFormVariables): Promise<FetchResult<CreateWarehouseLocation>> =>
      mutate({ variables: { input } }),
    [mutate]
  );

  return { create, loading, error };
}
